/* eslint-disable eag/match-named-export */
import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, MenuBadge, BadgeColorScheme} from 'platform/components';
import {Show, ThemeIconKey} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useLocation, useParams} from 'react-router-dom';

import {isNil, not} from 'ramda';
import {isNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {BusinessCaseResponseBody} from '@dms/api/finance';
import {
  businessCaseApi,
  useCreateOfferMutation,
  useCreateSaleVehicleMutation,
  useGetBusinessCaseQuery,
  useGetBusinessCaseTestDriveQuery,
  useGetVehicleQuery,
  useLazyGetVehicleV2Query,
  useListBusinessCaseTestDrivesQuery,
  useSwitchOfferSaleVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds, VehicleTypeEnumObject} from '@dms/api/shared';
import {
  saleVehicleApi,
  useGetSaleVehicleComplaintsQuery,
  useVehicleActiveReservationQuery,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {
  Checklists,
  ErrorPage,
  NavigationItem,
  Page,
  Section,
  VehicleReservations,
  getCustomerName,
  handleApiError,
  is404ApiError,
  useChecklist,
  useDocumentContextCount,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  usePermissions,
  PageV2,
} from '@dms/shared';
import {getVehicleMakeModels, useApiDispatch, useRouter, useThunkDispatch} from '@dms/teas';

import {buildArray, composePath, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {BusinessCaseDocuments} from '../../BusinessCaseDocuments';
import {BusinessCaseTabItem} from '../../BusinessCaseTabItem';
import {BusinessCaseActivityPanel} from '../../components/BusinessCaseActivityPanel/BusinessCaseActivityPanel';
import {BusinessCaseCheckout} from '../../components/BusinessCaseCheckout/BusinessCaseCheckout';
import {BusinessCaseHeader} from '../../components/BusinessCaseHeader/BusinessCaseHeader';
import {BusinessCaseOffersCreateVehicle} from '../../components/BusinessCaseOffers/BusinessCaseOffersCreateVehicle';
import {BusinessCaseOffersSelectVehicleDialog} from '../../components/BusinessCaseOffers/BusinessCaseOffersSelectVehicleDialog';
import {BusinessCasePurchaseVehicles} from '../../components/BusinessCaseOffers/BusinessCasePurchaseVehicles/BusinessCasePurchaseVehicles';
import {BusinessCaseSaleVehicles} from '../../components/BusinessCaseOffers/BusinessCaseSaleVehicles/BusinessCaseSaleVehicles';
import {BusinessCaseOverview} from '../../components/BusinessCaseOverview/BusinessCaseOverview';
import {BusinessCaseTestDrive} from '../../components/BusinessCaseTestDrive/BusinessCaseTestDrive';
import {BusinessCaseTestDriveV2} from '../../components/BusinessCaseTestDrive/BusinessCaseTestDriveV2';
import {SelectVehicleContextProvider} from '../../components/SelectVehicleContextProvider';
import {useSelectVehicleContext} from '../../hooks/useSelectVehicleContext';
import {BusinessCaseComplaintDetail} from '../BusinessCaseComplaintDetail/BusinessCaseComplaintDetail';
import {BusinessCaseInsurance} from '../BusinessCaseInsurance/BusinessCaseInsurance';
import {BusinessCaseOverview as BusinessCaseOverviewV2} from '../BusinessCaseOverview/BusinessCaseOverview';
import {BusinessCaseActivityLogs} from './(sections)/BusinessCaseActivityLogs/BusinessCaseActivityLogs';
import {BusinessCaseCustomerDetail} from './(sections)/BusinessCaseCustomer/BusinessCaseCustomerDetail';

type BusinessCaseDetailProps = {
  businessCase: BusinessCaseResponseBody;
};

const BusinessCaseDetail = (props: BusinessCaseDetailProps) => {
  const router = useRouter();
  const dispatch = useThunkDispatch();
  const apiDispatch = useApiDispatch();

  const [createSaleVehicle] = useCreateSaleVehicleMutation();
  const [switchToSaleVehicle] = useSwitchOfferSaleVehicleMutation();
  const [createOffer] = useCreateOfferMutation();
  const [getVehicle] = useLazyGetVehicleV2Query();
  const [businessCaseId] = router.params.dynamic;

  const location = useLocation();
  const hasPurchaseOffer =
    Boolean((location?.state as {offerId?: string})?.offerId) &&
    Boolean(
      (location?.state as {purchaseVehicleType?: 'IN_STOCK' | 'BROKERAGE'})?.purchaseVehicleType
    );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCase?.id ?? ''},
    {skip: isNilOrEmpty(props.businessCase?.id)}
  );
  const {data: customer} = useGetCustomerV2Query(
    {customerId: businessCase?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCase?.customerId)}
  );

  const allowedActions = businessCase?.actions;

  const {
    setCreateVehicleModalOpen,
    setSelectSaleVehicleOpen,
    isSelectSaleVehicleOpen,
    isVehicleModalOpen,
  } = useSelectVehicleContext();

  const toggleSelectVehicle = () => {
    setSelectSaleVehicleOpen(!isSelectSaleVehicleOpen);
  };

  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();

  const onAddVehicleToBuyClose = () => {
    setCreateVehicleModalOpen(false, 'IN_STOCK');
  };

  const offer = businessCase?.offers?.[0];
  const saleVehicle = offer?.saleVehicles?.[0];

  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: saleVehicle?.vehicleId ?? ''},
    {skip: isNilOrEmpty(saleVehicle?.vehicleId)}
  );

  const {data: complaints} = useGetSaleVehicleComplaintsQuery(
    {saleVehicleId: businessCase?.saleVehicleId ?? ''},
    {skip: isNil(businessCase?.saleVehicleId)}
  );

  const {
    data: vehicleActiveReservationData,
    error: vehicleReservationError,
    isError: isVehicleReservationError,
  } = useVehicleActiveReservationQuery(
    {
      vehicleId: saleVehicle?.vehicleId ?? '',
    },
    {
      skip: isNilOrEmpty(saleVehicle?.vehicleId) || businessCase?.businessCaseType !== 'SELLING',
    }
  );

  const {data: bcTestDrive} = useGetBusinessCaseTestDriveQuery(
    {
      businessCaseId,
    },
    {
      skip:
        businessCase?.businessCaseType !== 'SELLING' ||
        isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2),
    }
  );

  const {data: testDrives} = useListBusinessCaseTestDrivesQuery(
    {
      businessCaseId,
    },
    {
      skip:
        businessCase?.businessCaseType !== 'SELLING' ||
        !isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2),
    }
  );

  const shouldShowTestDriveBadge =
    businessCase?.businessCaseType === 'SELLING' &&
    isNotNilOrEmpty(bcTestDrive) &&
    bcTestDrive?.businessCaseId === businessCaseId;

  const shouldShowTestDriveBadgeV2 =
    businessCase?.businessCaseType === 'SELLING' && isNotNilOrEmpty(testDrives);

  const activeTestDrives = testDrives?.filter((testDrive) => testDrive.state !== 'cancelled') ?? [];

  const isAnyTestDrivePlanned = activeTestDrives.some((testDrive) => testDrive.state === 'planned');
  const hasAnyTestDrives = activeTestDrives.length > 0;

  const testDrivesBadge: MenuBadge | undefined =
    shouldShowTestDriveBadgeV2 && hasAnyTestDrives
      ? {
          icon: isAnyTestDrivePlanned ? 'action/watch_later' : 'navigation/check',
          colorScheme: isAnyTestDrivePlanned ? 'orange' : 'green',
        }
      : undefined;

  const shouldShowVehicleReservationBadge =
    businessCase?.businessCaseType === 'SELLING' &&
    !(isVehicleReservationError && is404ApiError(vehicleReservationError)) &&
    vehicleActiveReservationData?.businessCase?.id === businessCaseId;

  const documentsCountBadge = useDocumentContextCount('business-case', businessCase?.id ?? '');

  const {count: tasksTodoCount} = useTasksCount({
    entityId: businessCaseId,
    relation: EntityResourceIds.businessCase,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: businessCaseId,
    relation: EntityResourceIds.businessCase,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const navigate = useNavigate();

  const addVehicleToSell = async (vehicleId: string): Promise<void> => {
    if (!businessCaseId) {
      return;
    }

    const offerId = offer?.id;

    // Offer already exists
    if (offerId) {
      await createSaleVehicle({
        businessCaseId,
        offerId,
        offerSaleVehicleRequestBody: {vehicleId},
      })
        .unwrap()
        .catch(handleApiError);
    } else {
      await createOffer({
        businessCaseId,
        offerRequestBody: {vehicleToSaleId: vehicleId},
      });
    }

    const {data: vehicleDetail} = await getVehicle({vehicleId});

    if (vehicleDetail) {
      await apiDispatch(getVehicleMakeModels.action, {
        vehicleType: vehicleDetail.type || VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
        make: vehicleDetail?.make ?? undefined,
      });
    }

    navigate(composePath(businessCaseRoutes.selling, {params: {id: businessCaseId}}));
  };

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: saleVehicle?.vehicleId ?? '',
    },
    {skip: isNil(saleVehicle?.vehicleId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: businessCase?.id ?? '',
    },
    {skip: isNilOrEmpty(businessCase?.id)}
  );

  const [
    hasComplaintReadPermission,
    hasVehicleReadActiveReservationPermission,
    hasVehicleReadReservationHistoryPermission,
    canReadDocuments,
    hasReadTestDrivePermission,
  ] = usePermissions({
    permissionKeys: [
      'complaintRead',
      'vehicleReadActiveReservation',
      'vehicleReadReservationHistory',
      'readDocuments',
      'readTestDrive',
    ],
    scopes: {
      complaintRead: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      vehicleReadActiveReservation: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleReadReservationHistory: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: businessCaseId,
    resourceId: 'BUSINESS_CASE',
  });

  const testDriveBadge: MenuBadge | undefined = match<
    [boolean, boolean, boolean],
    MenuBadge | undefined
  >([
    hasReadTestDrivePermission,
    isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2),
    shouldShowTestDriveBadge,
  ])
    .with([true, true, Pattern.any], () => testDrivesBadge)
    .with(
      [Pattern.any, false, true],
      () =>
        ({
          icon: 'navigation/check' as ThemeIconKey,
          colorScheme: 'green' as BadgeColorScheme,
        }) as MenuBadge
    )
    .otherwise(() => undefined);

  const navigationItems: NavigationItem[] = buildArray<NavigationItem>([
    {
      id: BusinessCaseTabItem.OVERVIEW,
      label: i18n.t('page.businessCase.labels.overview'),
      content: isFeatureEnabled(featureFlags.BUSINESS_CASE_OVERVIEW) ? (
        <BusinessCaseOverviewV2 />
      ) : (
        <BusinessCaseOverview />
      ),
      href: composePath(businessCaseRoutes.overview, {params: {id: businessCaseId}}),
      hasSeparator: true,
      'data-testid': testIds.businessCase.detail('overview'),
    },
  ])
    .add({
      id: BusinessCaseTabItem.CUSTOMER,
      label: i18n.t('entity.customer.labels.customer'),
      content: <BusinessCaseCustomerDetail />,
      href: composePath(businessCaseRoutes.customer, {params: {id: businessCaseId}}),
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('customer'),
    })
    .add({
      id: BusinessCaseTabItem.BUYING,
      label: i18n.t('entity.businessCase.labels.buyingOrCommission'),
      content: <BusinessCasePurchaseVehicles />,
      href: composePath(businessCaseRoutes.buying, {params: {id: businessCaseId}}),
      isDisabled:
        not(allowedActions?.purchaseViewVehicleDetail) ||
        businessCase?.businessCaseType === 'SELLING',
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('buying'),
    })
    .add({
      id: BusinessCaseTabItem.SELLING,
      label: i18n.t('page.businessCase.labels.selling'),
      content: <BusinessCaseSaleVehicles />,
      href: composePath(businessCaseRoutes.selling, {params: {id: businessCaseId}}),
      isDisabled:
        not(allowedActions?.saleViewVehicleDetail) ||
        businessCase?.businessCaseType === 'BUYING' ||
        hasPurchaseOffer,
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('selling'),
    })
    .add({
      id: BusinessCaseTabItem.TEST_DRIVE,
      label: i18n.t('entity.businessCase.labels.testDrive'),
      content: isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) ? (
        <BusinessCaseTestDriveV2 />
      ) : (
        <BusinessCaseTestDrive />
      ),
      href: composePath(businessCaseRoutes.testDrive, {params: {id: businessCaseId}}),
      isDisabled:
        isNilOrEmpty(saleVehicle) ||
        (isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) && !hasReadTestDrivePermission),
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('testDrive'),
      badge: testDriveBadge,
    })
    .when(hasVehicleReadActiveReservationPermission && hasVehicleReadReservationHistoryPermission, {
      id: BusinessCaseTabItem.RESERVATIONS,
      label: i18n.t('entity.vehicle.labels.reservations'),
      content: (
        <VehicleReservations
          vehicleId={saleVehicle?.vehicleId ?? ''}
          customerId={customer?.id}
          businessCaseId={businessCaseId}
        />
      ),
      href: composePath(businessCaseRoutes.reservations, {params: {id: businessCaseId}}),
      isDisabled: not(saleVehicle?.vehicleId),
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('reservations'),
      badge: shouldShowVehicleReservationBadge
        ? {
            icon: 'navigation/check',
            colorScheme: 'green' as BadgeColorScheme,
          }
        : undefined,
    })
    .whenFeatureEnabled(featureFlags.SALES_INSURANCE_COMPARISON_V1, {
      id: BusinessCaseTabItem.INSURANCE,
      label: i18n.t('entity.insurance.labels.title'),
      content: <BusinessCaseInsurance />,
      href: composePath(businessCaseRoutes.insurance, {params: {id: businessCaseId}}),
      isDisabled:
        not(saleVehicle?.vehicleId) ||
        (businessCase?.businessCaseType !== 'SELLING' && businessCase?.businessCaseType !== 'SWAP'),
      hasCounter: true,
      'data-testid': testIds.businessCase.detail('insurance'),
    })
    .add({
      id: BusinessCaseTabItem.CHECKOUT,
      label: i18n.t('entity.businessCase.labels.checkout'),
      content: <BusinessCaseCheckout />,
      href: composePath(businessCaseRoutes.checkout, {params: {id: businessCaseId}}),
      isDisabled: not(allowedActions?.checkoutViewDetail),
      hasCounter: true,
      hasSeparator: true,
      'data-testid': testIds.businessCase.detail('checkout'),
    })
    .when(
      hasComplaintReadPermission &&
        isFeatureEnabled(featureFlags.SALES_CLAIMS) &&
        businessCase?.businessCaseType === 'SELLING',
      {
        id: 'businessCaseComplaints',
        label: i18n.t('entity.vehicle.labels.complaints'),
        content: <BusinessCaseComplaintDetail />,
        href: composePath(businessCaseRoutes.complaints, {params: {id: businessCaseId}}),
        badge: isPositive(complaints?.length)
          ? {
              value: complaints?.length ?? 0,
              colorScheme: 'neutral',
            }
          : undefined,
        'data-testid': testIds.businessCase.detail('complaints'),
      }
    )
    .when(canReadDocuments, {
      id: BusinessCaseTabItem.DOCUMENTS,
      label: i18n.t('page.businessCase.documents.title'),
      content: <BusinessCaseDocuments />,
      href: composePath(businessCaseRoutes.documents, {params: {id: businessCaseId}}),
      badge: documentsCountBadge,
      'data-testid': testIds.businessCase.detail('documents'),
      hasSeparator:
        isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT) ||
        isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS) ||
        isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: BusinessCaseTabItem.CHECKLISTS,
      label: i18n.t('entity.checklist.labels.title'),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="business-case"
            contextId={businessCaseId}
          />
        </Section>
      ),
      badge: badgeSettings,
      href: composePath(businessCaseRoutes.checklists, {params: {id: businessCaseId}}),
      'data-testid': testIds.businessCase.detail('checklists'),
      hasSeparator:
        isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT) ||
        isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS),
    })
    .whenFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT, {
      id: BusinessCaseTabItem.TASKS,
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.businessCase.detail('navigation-item-tasks'),
      href: composePath(businessCaseRoutes.tasks, {params: {id: businessCaseId}}),
      content: (
        <TaskGridPage
          gridCode="business-case-task"
          entityId={businessCaseId}
          data-testid={testIds.businessCase.detail('tasks')}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      hasSeparator: isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS),
    })
    .whenFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS, {
      id: 'activityLogs',
      href: composePath(businessCaseRoutes.activityLog, {params: {id: businessCaseId}}),
      label: i18n.t('general.labels.activityLogs'),
      content: [
        {route: businessCaseRoutes.activityLog, element: <BusinessCaseActivityLogs />},
        {route: businessCaseRoutes.emailActivityLogs, element: <BusinessCaseActivityLogs />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-activityLogs'),
    });

  const closeCustomerWidget = () => {
    setSelectSaleVehicleOpen(false);
  };

  const onSelectSaleVehicle = async (id: string) => {
    if (offer?.saleVehicles?.[0]) {
      await switchToSaleVehicle({
        businessCaseId,
        offerId: offer.id,
        vehicleId: id,
      })
        .unwrap()
        .catch(handleApiError);

      const {data: vehicleDetail} = await getVehicle({vehicleId: id});

      if (vehicleDetail) {
        await apiDispatch(getVehicleMakeModels.action, {
          vehicleType: vehicleDetail.type || VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
          make: vehicleDetail?.make ?? undefined,
        });
      }

      await dispatch(saleVehicleApi.endpoints.getSaleVehicle.initiate({vehicleId: id}));
      dispatch(
        businessCaseApi.util.invalidateTags([{type: 'BusinessCaseDetail', id: businessCaseId}])
      );
    } else {
      await addVehicleToSell(id);
      const vehicleData = await getVehicle({vehicleId: id});
      handleSearchAuthorizationProfiles(
        {
          vehicleMake: vehicleData?.data?.make ?? '',
          vehicleType: vehicleData?.data?.type ?? null,
          vehicleModelFamily: vehicleData?.data?.modelFamily ?? null,
          vehicleModelFamilyGroup: vehicleData?.data?.modelFamilyGroup ?? null,
        },
        businessCase?.customerId,
        {bcNumber: businessCase?.code, bcCreatedAt: businessCase?.createdAt}
      );
    }
    toggleSelectVehicle();
  };

  const PageComponent = isFeatureEnabled(featureFlags.ENTITY_HEADER_V2) ? PageV2 : Page;

  return (
    <>
      <Helmet title={i18n.t('page.businessCase.title', {name: `${getCustomerName(customer)}`})} />

      <PageComponent
        isFullHeight
        navigation={navigationItems}
        onNavigate={closeCustomerWidget}
        data-testid={testIds.businessCase.home('content')}
        header={<BusinessCaseHeader />}
        activityPanel={<BusinessCaseActivityPanel businessCaseId={businessCaseId} />}
      />

      <Show when={isSelectSaleVehicleOpen}>
        <BusinessCaseOffersSelectVehicleDialog
          isOpen={isSelectSaleVehicleOpen}
          onSubmit={onSelectSaleVehicle}
          onClose={toggleSelectVehicle}
        />
      </Show>

      <Show when={isVehicleModalOpen}>
        <BusinessCaseOffersCreateVehicle onClose={onAddVehicleToBuyClose} />
      </Show>
    </>
  );
};

export const BusinessCaseDetailWrapper = () => {
  const {id: businessCaseId} = useParams();
  const {data: businessCase, isError} = useGetBusinessCaseQuery(
    {businessCaseId: businessCaseId ?? ''},
    {skip: isNilOrEmpty(businessCaseId)}
  );

  if (isNilOrEmpty(businessCaseId)) {
    return <ErrorPage message={i18n.t('page.businessCase.notifications.notFound')} />;
  }

  return (
    <DataStatus isError={isError}>
      <SelectVehicleContextProvider>
        {businessCase && <BusinessCaseDetail key={businessCaseId} businessCase={businessCase} />}
      </SelectVehicleContextProvider>
    </DataStatus>
  );
};
